import { Feature, getFeatureSet } from "../phonemes/features";
import { IpaType } from "../phonemes/ipa";
import { PhonemeItem } from "../phonemes/segmentPhonemes";
import { intoSegments, SegmentStrategy } from "../rules/intoSegments";
import { Pattern } from "./pattern";
import { allSpecials, PhonemeInfo } from "./phonemeInfo";

/** /ipa/, :feature, $text, #special, ^lookup, e.g. /dʒ/, :bilabial, $ABC, #word, ^V */
export type IpaOrFeature = string;

export interface ConvertPatternError {
  readonly error: 'unknown ipa' | 'unknown feature' | 'unknown special' | 'requires one of / %/ : $ %$ # ^';
  readonly content: IpaType[];
}
export function convertPatternErrorToString(error: ConvertPatternError): string {
  return `${error.error} ${error.content.join(', ')}`;
}

export interface ConvertResults {
  pattern: Pattern<PhonemeInfo>;
  errors: ConvertPatternError[];
}

/** segment phonemes, convert features (optionally recognize multi-symbol affricates like tʃ) */
export function convertPattern(input: Pattern<IpaOrFeature>, affricates?: 'all-aff' | 'en-aff', strategy?: SegmentStrategy): ConvertResults {
  if (typeof input === 'string') {
    const result = convertIpaOrFeature(input, affricates, strategy);
    return 'error' in result
      ? { pattern: { raw: [] }, errors: [result] }
      : { pattern: result, errors: [] };
  } else if ('seq' in input) {
    const results = input.seq.map(p => convertPattern(p, affricates, strategy));
    const [seq, errors] = separate(results);
    return { pattern: { seq }, errors };
  } else if ('and' in input) {
    const results = input.and.map(p => convertPattern(p, affricates, strategy));
    const [and, errors] = separate(results);
    return { pattern: { and }, errors };
  } else if ('or' in input) {
    const results = input.or.map(p => convertPattern(p, affricates, strategy));
    const [or, errors] = separate(results);
    return { pattern: { or }, errors };
  } else if ('opt' in input) {
    const results = convertPattern(input.opt, affricates, strategy);
    return { pattern: { opt: results.pattern }, errors: results.errors };
  } else if ('store' in input) {
    const results = convertPattern(input.store, affricates, strategy);
    return { pattern: { store: results.pattern }, errors: results.errors };
  }
  const results = convertPattern(input.not, affricates, strategy);
  return { pattern: { not: results.pattern }, errors: results.errors };
}

function separate(results: ConvertResults[]): [Pattern<PhonemeInfo>[], ConvertPatternError[]] {
  const pattern = results.map(p => p.pattern);
  const errors = results.filter(p => p.errors.length > 0).flatMap(p => p.errors);
  return [pattern, errors];
}

function convertIpaOrFeature(iof: IpaOrFeature, affricates?: 'all-aff' | 'en-aff', strategy?: SegmentStrategy): PhonemeInfo | ConvertPatternError {
  const first = iof[0];
  if (first === ':') {
    const feature = iof.substring(1) as Feature;
    if (!getFeatureSet().has(feature)) {
      return { error: 'unknown feature', content: [feature] };
    }
    return { feature: [feature as Feature] };
  }

  if (first === '/' || iof.startsWith('%/')) {
    const start = first === '%' ? 2 : 1;
    const raw = iof.endsWith('/') ? iof.substring(start, iof.length - 1) : iof.substring(start);
    const [hasUnknowns, phonemes] = trySegment(raw, strategy, affricates);
    if (hasUnknowns) {
      const content = phonemes.filter(p => p.meta === 'unknown').map(p => p.phoneme);
      return { error: 'unknown ipa', content };
    }
    const ipa = phonemes.map(p => p.phoneme);
    return first === '%' ? { ipa: ipa, subset: true } : { ipa: ipa };
  }

  if (first === '$' || iof.startsWith('%$')) {
    const a: string[] = [];
    for (let i = 0; i < iof.length; i++) {
      if (i > 1 || (i === 1 && first !== '%')) {
        a.push(iof[i]);
      }
    }
    return first === '%' ? { raw: a, subset: true } : { raw: a };
  }

  if (first === '#') {
    const special = iof.substring(1);
    if (!allSpecials.includes(special)) {
      return { error: 'unknown special', content: [special] };
    }
    return { special } as PhonemeInfo;
  }

  if (first === '^') {
    const name = iof.substring(1);
    return { name } as PhonemeInfo;
  }

  if (iof === '' || iof === '∅') {
    return { empty: '' };
  }

  return { error: 'requires one of / %/ : $ %$ # ^', content: [iof] };
}

/** prefer segmenting as IPA, if that fails try what we were told */
function trySegment(
  raw: string, strategy?: SegmentStrategy, affricates?: "all-aff" | "en-aff"
): [boolean, PhonemeItem[]] {
  const [hasUnknowns, phonemes] = intoSegments(raw, SegmentStrategy.Ipa, affricates);
  if (!hasUnknowns || strategy === SegmentStrategy.Ipa || strategy === undefined) {
    return [hasUnknowns, phonemes];
  }
  return intoSegments(raw, strategy, affricates);
}
